/**
 * Tunable basic-game constants (rule.md §3–§7). All values are placeholders
 * marked _(tuning)_ in the rules; centralised here so balancing is one edit.
 */

/** Hit points per stack (rule.md §4). */
export const STACK_HP = 3;

/** Total stacks in a player's pool — pool = 30 pieces = 10 stacks × 3 HP. */
export const POOL_STACKS = 10;

/** Stacks deployed at setup; the rest start in reserve (rule.md §3). */
export const START_DEPLOYED = 4;
export const START_RESERVE = POOL_STACKS - START_DEPLOYED; // 6

/** Action points granted each turn (rule.md §5). */
export const AP_PER_TURN = 2;

/** Zone of Control bites only within this hex-radius of a home cell; the neutral
 *  midfield is a free race (a unit there may run past enemies). 0 disables zones (rule.md §6). */
export const HOME_ZONE_RADIUS = 1;

/** Hard turn cap before scoring by tie-break (rule.md §4). */
export const TURN_CAP = 40;

/** Number of basic elements (the cooldown pool size, rule.md §7.3). */
export const ELEMENT_COUNT = 5;

export type ActionType = "move" | "attack" | "deploy";

/**
 * Default per-turn caps per action type, and faction overrides (rule.md §6).
 * A cap is the max times that action type may be taken in a turn, itself
 * bounded by AP. Faction order: beast / night / federation.
 */
import type { Faction } from "../types/common";

export const DEFAULT_ACTION_CAPS: Readonly<Record<ActionType, number>> = {
  move: 1,
  attack: 2,
  deploy: 1,
} as const;

const FACTION_CAP_OVERRIDES: Readonly<
  Record<Faction, Partial<Record<ActionType, number>>>
> = {
  beast: { move: 2 }, // mobility
  night: { deploy: 2 }, // mass + siege: wins standoffs assaulting a home zone (attack.applier, §9)
  federation: {}, // no cap edge — Bastion trait instead (rule.md §9)
};

export function actionCap(faction: Faction, action: ActionType): number {
  return FACTION_CAP_OVERRIDES[faction][action] ?? DEFAULT_ACTION_CAPS[action];
}
